import { ChevronRight } from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { formatAmount, formatCurrency } from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';

import { SplitFiestaLoading } from '@/components/common/SplitFiestaLoading';
import {
  SECOND_SERIES_DASH,
  squareDot,
} from '@/components/reports/chartSeries';

import {
  useGroupMonthlyReportQuery,
  useGroupSpendingReportQuery,
} from '@/hooks/queries/useReports';

interface ChartData {
  month: string;
  monthLabel: string;
  totalAmount: number;
  expenseCount: number;
  averagePerExpense: number;
  categories: Record<string, number>;
}

// Custom tooltip component
interface CustomTooltipProps {
  active?: boolean;
  payload?: ReadonlyArray<{
    name?: string;
    value: number;
    color: string;
  }>;
  label?: string | number;
  t: (key: string) => string;
  translateCategory: (category: string) => string;
}

function CustomTooltip({
  active,
  payload,
  label,
  t,
  translateCategory,
}: CustomTooltipProps) {
  if (active && payload && payload.length > 0) {
    return (
      <div className="bg-white dark:bg-gray-800 p-2 sm:p-3 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-w-[85vw] sm:max-w-none">
        <p className="font-medium text-gray-900 dark:text-white mb-2 text-sm sm:text-base">
          {label}
        </p>
        {payload.map((entry, index: number) => (
          <div
            key={index}
            className="flex items-center justify-between gap-2 text-xs sm:text-sm"
          >
            <div className="flex items-center gap-1.5 min-w-0 flex-shrink">
              <div
                className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-xs flex-shrink-0"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-gray-600 dark:text-gray-300 truncate">
                {entry.name === 'totalAmount'
                  ? t('reports:monthly.totalAmount')
                  : entry.name === 'expenseCount'
                    ? t('reports:monthly.expenseCount')
                    : entry.name === 'averagePerExpense'
                      ? t('reports:monthly.averagePerExpense')
                      : translateCategory(entry.name ?? '')}
                :
              </span>
            </div>
            <span className="font-medium text-gray-900 dark:text-white flex-shrink-0">
              {entry.name === 'expenseCount'
                ? entry.value
                : formatAmount(entry.value)}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
}

// Colors for categories
const categoryColors = [
  '#f97316', // orange-500
  '#3b82f6', // blue-500
  '#10b981', // emerald-500
  '#f59e0b', // amber-500
  '#ef4444', // red-500
  '#8b5cf6', // violet-500
  '#06b6d4', // cyan-500
  '#84cc16', // lime-500
  '#f43f5e', // rose-500
  '#6366f1', // indigo-500
];

interface SpendingSummaryProps {
  groupId: string;
  startDate: string;
  endDate: string;
  onSelectCategory?: (category: string) => void;
}

export default function SpendingSummary({
  groupId,
  startDate,
  endDate,
  onSelectCategory,
}: SpendingSummaryProps) {
  const { t, i18n } = useTranslation();
  useNamespace('reports');
  useNamespace('expenses');

  // Convert date (YYYY-MM-DD) to month (YYYY-MM)
  const startMonth = startDate.substring(0, 7);
  const endMonth = endDate.substring(0, 7);

  const { data: report, isLoading } = useGroupSpendingReportQuery(
    groupId,
    startDate,
    endDate
  );

  const { data: monthlyData, isLoading: monthlyLoading } =
    useGroupMonthlyReportQuery(groupId, startMonth, endMonth, true);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <SplitFiestaLoading className="bg-transparent" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="p-8 text-center text-gray-500 dark:text-gray-400">
        {t('reports:noData')}
      </div>
    );
  }

  // Helper function to translate category names
  const translateCategory = (category: string): string => {
    const categoryKey = `expenses:categories.${category}`;
    const translation = t(categoryKey);
    return translation === categoryKey
      ? category.charAt(0).toUpperCase() + category.slice(1)
      : translation;
  };

  // Memoized tooltip render function
  const renderTooltip = (props: Record<string, unknown>) => (
    <CustomTooltip
      active={props.active as boolean | undefined}
      payload={props.payload as CustomTooltipProps['payload']}
      label={props.label as string | number | undefined}
      t={t}
      translateCategory={translateCategory}
    />
  );

  // Transform monthly data for charts
  const chartData: ChartData[] =
    monthlyData
      ?.slice()
      .reverse()
      .map(month => ({
        month: month.month,
        monthLabel: (() => {
          const [year, monthNum] = month.month.split('-');
          const date = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
          return date.toLocaleDateString(i18n.language, {
            month: 'short',
            year: 'numeric',
          });
        })(),
        totalAmount: month.totalAmount,
        expenseCount: month.expenseCount,
        averagePerExpense: month.averagePerExpense,
        categories: month.categories,
      })) || [];

  // Get all unique categories across all months
  const allCategories = monthlyData
    ? Array.from(
        new Set(monthlyData.flatMap(month => Object.keys(month.categories)))
      )
    : [];

  // Prepare data for stacked category chart
  const categoryChartData = chartData.map(month => ({
    ...month,
    ...Object.fromEntries(
      allCategories.map(category => [category, month.categories[category] || 0])
    ),
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t('reports:summary.title')}
        </h2>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <div className="bg-linear-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 px-4 py-4 rounded-lg min-w-0">
          <p className="text-sm text-blue-600 dark:text-blue-400 mb-1">
            {t('reports:summary.totalSpent')}
          </p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-900 dark:text-blue-100">
            {formatCurrency(report.totalExpenses, report.currency)}
          </p>
        </div>

        <div className="bg-linear-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 px-4 py-4 rounded-lg min-w-0">
          <p className="text-sm text-green-600 dark:text-green-400 mb-1">
            {t('reports:summary.averageExpense')}
          </p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-green-900 dark:text-green-100">
            {formatCurrency(report.averageExpenseAmount, report.currency)}
          </p>
        </div>

        <div className="bg-linear-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 px-4 py-4 rounded-lg min-w-0">
          <p className="text-sm text-purple-600 dark:text-purple-400 mb-1">
            {t('reports:summary.totalExpenses')}
          </p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-purple-900 dark:text-purple-100">
            {report.expenseCount}
          </p>
        </div>

        <div className="bg-linear-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 px-4 py-4 rounded-lg min-w-0">
          <p className="text-sm text-orange-600 dark:text-orange-400 mb-1">
            {(() => {
              if (report.categoryInsights.length === 0) {
                return t('reports:summary.topCategory');
              }
              const topCategory = report.categoryInsights.reduce((max, cat) =>
                cat.total > max.total ? cat : max
              );
              return t(`expenses:categories.${topCategory.category}`);
            })()}
          </p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-orange-900 dark:text-orange-100">
            {report.categoryInsights.length === 0
              ? formatCurrency(0, report.currency)
              : formatCurrency(
                  Math.max(...report.categoryInsights.map(cat => cat.total)),
                  report.currency
                )}
          </p>
        </div>
      </div>

      {/* Category Insights */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('reports:summary.topCategories')}
        </h3>
        {report.categoryInsights.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <p>{t('reports:noData')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {report.categoryInsights.slice(0, 5).map(category => {
              const name = t(`expenses:categories.${category.category}`);
              const body = (
                <>
                  <div className="min-w-0 flex-1 text-left">
                    <p className="font-medium text-gray-900 dark:text-white text-sm sm:text-base truncate">
                      {name}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                      {category.count} {t('reports:summary.transactions')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="text-right">
                      <p className="font-semibold text-gray-900 dark:text-white text-xs sm:text-sm lg:text-base">
                        {formatCurrency(category.total, report.currency)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {category.percentage > 0 && category.percentage < 0.1
                          ? '<0.1%'
                          : `${category.percentage.toFixed(1)}%`}
                      </p>
                    </div>
                    {onSelectCategory ? (
                      <ChevronRight
                        aria-hidden="true"
                        className="w-4 h-4 text-gray-400"
                      />
                    ) : null}
                  </div>
                </>
              );
              const rowClass =
                'flex items-center justify-between gap-4 px-3 sm:px-4 py-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg';
              return onSelectCategory ? (
                <button
                  key={category.category}
                  type="button"
                  data-testid={`summary-category-${category.category}`}
                  aria-label={t('reports:drilldown.openCategory', { name })}
                  onClick={() => onSelectCategory(category.category)}
                  className={`${rowClass} w-full hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500`}
                >
                  {body}
                </button>
              ) : (
                <div key={category.category} className={rowClass}>
                  {body}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Monthly Charts Section */}
      {monthlyLoading ? (
        <div className="flex justify-center items-center h-64">
          <SplitFiestaLoading className="bg-transparent" />
        </div>
      ) : (
        chartData.length > 0 && (
          <>
            {/* Monthly Spending Trend - Area Chart */}
            <div className="bg-transparent p-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t('reports:monthly.spendingTrend')}
              </h3>
              <div className="h-80 w-full min-w-0">
                <ResponsiveContainer width="100%" height={320}>
                  <AreaChart
                    data={chartData}
                    margin={{ left: 0, right: 0, top: 5, bottom: 20 }}
                  >
                    <defs>
                      <linearGradient
                        id="spendingGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#f97316"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="95%"
                          stopColor="#f97316"
                          stopOpacity={0.05}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#374151"
                      opacity={0.3}
                    />
                    <XAxis
                      dataKey="monthLabel"
                      stroke="#6b7280"
                      tick={{ fontSize: 11 }}
                      angle={-45}
                      textAnchor="end"
                      height={35}
                    />
                    <YAxis
                      stroke="#6b7280"
                      tick={{ fontSize: 10 }}
                      tickFormatter={value => formatAmount(value)}
                      width="auto"
                    />
                    <Tooltip
                      content={renderTooltip}
                      allowEscapeViewBox={{ x: false, y: false }}
                      wrapperStyle={{ zIndex: 50 }}
                      cursor={false}
                    />
                    <Area
                      type="monotone"
                      dataKey="totalAmount"
                      stroke="#f97316"
                      strokeWidth={2}
                      fill="url(#spendingGradient)"
                      dot={{ fill: '#f97316', r: 4 }}
                      activeDot={{ r: 6, stroke: '#f97316', strokeWidth: 2 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Category Breakdown - Stacked Bar Chart */}
            <div className="bg-transparent p-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t('reports:monthly.categoryBreakdown')}
              </h3>
              <div className="h-80 w-full min-w-0">
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart
                    data={categoryChartData}
                    margin={{ left: 0, right: 0, top: 5, bottom: 20 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#374151"
                      opacity={0.3}
                    />
                    <XAxis
                      dataKey="monthLabel"
                      stroke="#6b7280"
                      tick={{ fontSize: 11 }}
                      angle={-45}
                      textAnchor="end"
                      height={35}
                    />
                    <YAxis
                      stroke="#6b7280"
                      tick={{ fontSize: 10 }}
                      tickFormatter={value => formatAmount(value)}
                      width="auto"
                    />
                    <Tooltip
                      content={renderTooltip}
                      allowEscapeViewBox={{ x: false, y: false }}
                      wrapperStyle={{ zIndex: 50 }}
                      cursor={false}
                    />
                    {allCategories.map((category, index) => (
                      <Bar
                        key={category}
                        dataKey={category}
                        stackId="categories"
                        fill={categoryColors[index % categoryColors.length]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Category Legend */}
              <div className="flex flex-wrap gap-3 mt-4 pt-4">
                {allCategories.map((category, index) => (
                  <div key={category} className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-xs"
                      style={{
                        backgroundColor:
                          categoryColors[index % categoryColors.length],
                      }}
                    />
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      {translateCategory(category)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Expense Count & Average - Line Chart */}
            <div className="bg-transparent p-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t('reports:monthly.activityTrends')}
              </h3>
              <div className="h-80 w-full min-w-0">
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart
                    data={chartData}
                    margin={{ left: 0, right: 0, top: 5, bottom: 20 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#374151"
                      opacity={0.3}
                    />
                    <XAxis
                      dataKey="monthLabel"
                      stroke="#6b7280"
                      tick={{ fontSize: 11 }}
                      angle={-45}
                      textAnchor="end"
                      height={35}
                    />
                    <YAxis
                      yAxisId="count"
                      stroke="#6b7280"
                      tick={{ fontSize: 10 }}
                      width="auto"
                    />
                    <YAxis
                      yAxisId="average"
                      orientation="right"
                      stroke="#6b7280"
                      tick={{ fontSize: 10 }}
                      tickFormatter={value => formatAmount(value)}
                      width="auto"
                    />
                    <Tooltip
                      content={renderTooltip}
                      allowEscapeViewBox={{ x: false, y: false }}
                      wrapperStyle={{ zIndex: 50 }}
                      cursor={false}
                    />
                    <Line
                      yAxisId="count"
                      type="monotone"
                      dataKey="expenseCount"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={{ fill: '#3b82f6', r: 4 }}
                      activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2 }}
                    />
                    <Line
                      yAxisId="average"
                      type="monotone"
                      dataKey="averagePerExpense"
                      stroke="#10b981"
                      strokeWidth={2}
                      strokeDasharray={SECOND_SERIES_DASH}
                      dot={squareDot('#10b981', 7)}
                      activeDot={{ r: 6, stroke: '#10b981', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Legend for dual axis chart */}
              <div className="flex justify-center gap-6 mt-4 pt-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500" />
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    {t('reports:monthly.expenseCount')} (Left Axis)
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-xs bg-green-500" />
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    {t('reports:monthly.avgPerExpense')} (Right Axis)
                  </span>
                </div>
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
}
